import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { IonContent, IonLoading, IonCard, IonCardContent, IonGrid, IonRow, IonCol, IonButton } from '@ionic/react';

// Components
import { HeaderComponent, DetailComponent, MobileDetailMessageComponent, WebDetailMessageComponent } from '../../../components';

// Helpers
import { extractConfigPage, loadPageInit, isNumber, isBranchEnabled, isWebView, saveModal, clearModal, sendEventTransaction } from '../../../helpers';

// Contexts
import { AppContext } from '../../../contexts';

// Validators
import { RoutesValidator } from './../../../validators';

// Constants
import { APP_CONFIG } from '../../../constants';

// Actions
import { resetStateAction, startUpdateTransactionAction, resetPageRefAction, resetErrorAction } from '../../../redux';

// Hooks
import { useForm, useValidatorFormDetail } from '../../../hooks';

// Scss
import './DetailPage.scss';

export const DetailPage = () => {
    
    const dispatch = useDispatch();
    const { activated } = useSelector(state => state.transaction);
    const { error, pageRef } = useSelector(state => state.app);

    const history = useHistory();
    const location = useLocation();
    const pageConfig = extractConfigPage(location);
    const { isWeb } = useContext(AppContext);
    const isMobileWebView = !isWeb && isWebView();
    const prevPage = loadPageInit();
    const [showModal, setShowModal] = useState({ show: false, error: null, status: null });
    const [loading, setLoading] = useState(false);

    const [{ amount }, handleInputChange, reset] = useForm({ amount: activated?.amount || '' });
    const { validation, inputs } = useValidatorFormDetail(pageConfig, amount);

    /**
     * Calcula en tiempo real el descuento neto y el importe final con descuento.
     * Retorna null cuando el importe es 0/vacío o el porcentaje del beneficio no es válido.
     * La fórmula es idéntica a la usada en handleConfirm para garantizar consistencia.
     */
    const discountPreview = useMemo(() => {
        const parsedAmount = parseFloat(amount);
        if (!parsedAmount || parsedAmount <= 0) return null;

        const type = activated.benefit?.type;
        if (!type || !type.includes('%')) return null;

        const percent = parseFloat(type.replace('%', '').trim());
        if (!isNumber(percent)) return null;

        const withDiscount = parseFloat((parsedAmount * ((100 - percent) / 100)).toFixed(2));
        const saved = parseFloat((parsedAmount - withDiscount).toFixed(2));

        return { saved, withDiscount };
    }, [amount, activated.benefit]);

    /**
     * Determina si el tipo de beneficio permite precalcular un descuento monetario.
     * Solo es true para tipos porcentuales (ej. "20%"). Para tipos como "2x1" retorna false.
     */
    const hasCalculableDiscount = useMemo(() => {
        const type = activated.benefit?.type;
        if (!type || !type.includes('%')) return false;
        const percent = parseFloat(type.replace('%', '').trim());
        return isNumber(percent);
    }, [activated.benefit]);

    useEffect(() => {

        RoutesValidator.validatePageDetail(activated, history);

    }, [activated]);

    /**
     * En caso de ocurrir cualquier error al llamar a la API para confirmar la transacción; muestra el mensaje
     */
    useEffect(() => {

        if (error) {

            setShowModal({ show: true, error: error?.message || '' });
            saveModal(true);
            dispatch(resetErrorAction());
        }

    }, [error]);

    /**
     * Maneja que una vez que la transacción se haya aprobado o cancelado, muestra el modal correcto
     * La propiedad approved se setea únicamente luego de una actualización. En caso de venir del historial,
     * la misma no existirá, para evitar que muestre el modal al cargar la página
     */
    useEffect(() => {
        
        // Si se viene de la página del historial, reseteo la referencia para evitar loop en los siguientes render
        // Si se trató de un confirmar o cancelar, valido si hay error o no
        if ( pageRef !== APP_CONFIG.PAGES.HISTORY.ref ) {

            if (activated.transactionStatus === APP_CONFIG.API.VIRTUAL_CREDENTIAL.APPROVED_CODE && activated.approved) {
    
                setShowModal({ show: true, error: null });
                saveModal(true);
            }
            else if (activated.transactionStatus === APP_CONFIG.API.VIRTUAL_CREDENTIAL.CANCELLED_CODE && !activated.approved) {
    
                handleFinish();
            }
        }
        else {

            dispatch(resetPageRefAction());
        }
        
    }, [activated]);

    /**
     * Determina si se puede editar el importe de la transacción
     */
    const enabledEditAmount = () => {

        return APP_CONFIG.PAGES.DETAIL.validate.transactionStatus.includes(activated.transactionStatus)
        && isBranchEnabled();
    }

    /**
     * Valida el formulario y llama a la API para Confirmar la transacción
     * @param {Object} e Evento del formulario
     */
    const handleConfirm = async (e) => {

        e.preventDefault();

        if (validation.formComplete && validation.formValid
            && enabledEditAmount()) {

            const percent = activated.benefit.type.replace('%', '').trim();

            const transaction = {
                ...activated,
                amount: parseFloat(amount),
                discountAmount: isNumber(percent) ? parseFloat((parseFloat(amount) * ((100 - percent)/100)).toFixed(2)) : 0,
                transactionStatus: APP_CONFIG.API.VIRTUAL_CREDENTIAL.APPROVED_CODE
            };
            
            dispatch(startUpdateTransactionAction(transaction, setLoading));
        }
    };

    /**
     * Cancela la transacción activa
     */
    const handleCancel = async () => {

        const transaction = {
            ...activated,
            transactionStatus: APP_CONFIG.API.VIRTUAL_CREDENTIAL.CANCELLED_CODE
        };

        dispatch(startUpdateTransactionAction(transaction, setLoading));
    };

    /**
     * Maneja el evento de cierre del mensaje final
     * Inicializa el store y redirecciona a la página inicial de la que vino el usuario
     */
    const handleFinish = () => {

        // Analytics
        if ( activated.approved ) {

            sendEventTransaction();
        }

        dispatch(resetStateAction());
        clearModal();
        history.push(prevPage);
    }

    return (

        <>
            <HeaderComponent
                title={pageConfig.title}
                urlRedirect={prevPage}
            />

            <IonLoading
                isOpen={loading}
                message={APP_CONFIG.CONTROLS.LOADING.MESSAGE}
            />

            {/* PAGE - MOBILE */}
            {!showModal.show && !isWeb &&
                <IonContent className={`ion-padding ion-text-center${isMobileWebView ? ' detail-content--webview' : ''}`}>

                    <DetailComponent
                        pageConfig={pageConfig}
                        transactionNumber={activated.transactionNumber}
                        subscriber={activated.subscriber}
                        benefit={activated.benefit}
                        transactionStatus={activated.transactionStatus}
                        setShowModal={setShowModal}
                        amount={amount}
                        inputs={inputs}
                        validation={validation}
                        handleConfirm={handleConfirm}
                        handleCancel={handleCancel}
                        handleInputChange={handleInputChange}
                        enabledEditAmount={enabledEditAmount}
                        discountPreview={discountPreview}
                        hasCalculableDiscount={hasCalculableDiscount}
                        showActions={!isMobileWebView}
                    />

                </IonContent>
            }

            {!showModal.show && isMobileWebView && enabledEditAmount() &&
                <div className="detail-webview-actions" data-testid="detail-webview-actions" role="group" aria-label="Acciones de transacción">
                    <IonButton
                        type="button"
                        shape="round"
                        size="undefined"
                        color="danger"
                        onClick={handleCancel}
                    >
                        CANCELAR
                    </IonButton>
                    <IonButton
                        type="button"
                        shape="round"
                        size="undefined"
                        disabled={!validation?.formComplete || !validation?.formValid}
                        onClick={handleConfirm}
                    >
                        CONFIRMAR
                    </IonButton>
                </div>
            }

            {/* PAGE - WEB */}
            {!showModal.show && isWeb &&
                <IonContent className="ion-padding ion-text-center">
                    <IonGrid>
                        <IonRow className='ion-text-left'>
                            <IonCol>
                                <h1>Detalle de transacción</h1>
                            </IonCol>
                        </IonRow>
                        <IonRow>
                            <IonCol>
                                <IonCard>
                                    <IonCardContent className="ion-text-center">
                                        <DetailComponent
                                            pageConfig={pageConfig}
                                            transactionNumber={activated.transactionNumber}
                                            subscriber={activated.subscriber}
                                            benefit={activated.benefit}
                                            transactionStatus={activated.transactionStatus}
                                            setShowModal={setShowModal}
                                            amount={amount}
                                            inputs={inputs}
                                            validation={validation}
                                            handleConfirm={handleConfirm}
                                            handleCancel={handleCancel}
                                            handleInputChange={handleInputChange}
                                            enabledEditAmount={enabledEditAmount}
                                            discountPreview={discountPreview}
                                            hasCalculableDiscount={hasCalculableDiscount}
                                        />
                                    </IonCardContent>
                                </IonCard>
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                </IonContent>
            }

            {/* WEB - MODAL */}
            {showModal.show && isWeb &&

                <WebDetailMessageComponent
                    message="¡La transacción ha sido exitosa!"
                    subtitle={`ID ${activated.transactionNumber}`}
                    showModal={showModal}
                    handleFinish={handleFinish}
                    // 5.1-5.2 Construir y pasar transactionSummary desde activated
                    transactionSummary={activated.benefit ? {
                        benefitType: activated.benefit.type,
                        benefitTitle: activated.benefit.title,
                        amount: activated.amount,
                        discountAmount: activated.discountAmount,
                        hasCalculableDiscount,
                    } : undefined}
                />
            }

            {/* MOBILE - MODAL */}
            {showModal.show && !isWeb &&

                <MobileDetailMessageComponent
                    message="¡La transacción ha sido exitosa!"
                    subtitle={`ID ${activated.transactionNumber}`}
                    showModal={showModal}
                    handleFinish={handleFinish}
                    // 5.1-5.2 Construir y pasar transactionSummary desde activated
                    transactionSummary={activated.benefit ? {
                        benefitType: activated.benefit.type,
                        benefitTitle: activated.benefit.title,
                        amount: activated.amount,
                        discountAmount: activated.discountAmount,
                        hasCalculableDiscount,
                    } : undefined}
                />
            }
        </>
    )
}
